import { useEffect, useState } from 'react';

/**
 * Height (in CSS px) that the on-screen keyboard currently overlaps the
 * viewport bottom.
 *
 * The Capacitor keyboard runs in `resize: 'none'` (overlay) mode so the fixed
 * poker-table layout never reflows/scales when the keyboard opens. The cost is
 * that anything anchored near the bottom (chat, bet tray, modal inputs) would
 * sit *under* the keyboard — so those components read this offset and lift
 * themselves above it.
 *
 * Derived from the visualViewport API: the difference between the layout
 * viewport height and the (shrunken) visual viewport is the keyboard height.
 * The 50px floor ignores small fluctuations (browser chrome, rubber-banding).
 */
export const useKeyboardOffset = (): number => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const update = () => {
      const overlap = window.innerHeight - vv.height - vv.offsetTop;
      setOffset(overlap > 50 ? Math.round(overlap) : 0);
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return offset;
};

export default useKeyboardOffset;
